import {Discipline, Event, MapEvent} from '@/apiTypes/event';
import {HIDDEN_EVENT_TYPES} from '@/constants/eventTypes';
import {
  buildTsQueryString,
  searchEventIds,
} from '@/helpers/fullTextSearch.server';
import {prismaClient} from '@/helpers/prismaClient.server';
import {createServerFn} from '@tanstack/react-start';
import {Prisma} from '@wayra/database';
import {z} from 'zod';

const validEventTypes = ['race', 'ride', 'route'] as const;
type EventTypeValue = (typeof validEventTypes)[number];

type BoundsData = {
  southWest: {
    lat: number;
    lng: number;
  };
  northEast: {
    lat: number;
    lng: number;
  };
  page?: number;
  pageSize?: number;
  q?: string;
  eventType?: EventTypeValue;
  disciplines?: Discipline[];
  dateFrom?: string;
  dateTo?: string;
  minDistance?: number;
  maxDistance?: number;
};

/** Shared include clause for hydrating events with their relations.
 * Excludes track_points and waypoints to keep the payload small. */
const eventInclude = {
  routes: {
    select: {
      id: true,
      createdAt: true,
      updatedAt: true,
      name: true,
      description: true,
      distance_meters: true,
      elevation_gain_meters: true,
      routeFileUrl: true,
      eventId: true,
    },
  },
  categories: {
    include: {
      prices: true,
    },
  },
  organizer: true,
} as const;

/** Minimal select for map event dots (non-page-1 results) */
const ghostEventSelect = {
  id: true,
  lat: true,
  lng: true,
  name: true,
  eventType: true,
  discipline: true,
  startDate: true,
  endDate: true,
  location: true,
  heroUrl: true,
  editorialContent: true,
  routes: {
    select: {
      distance_meters: true,
      elevation_gain_meters: true,
    },
  },
  organizer: {
    select: {
      name: true,
    },
  },
} as const;

export const getEventsByBounds = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) => {
    if (data === null || typeof data !== 'object') {
      throw new Error('data must be an object');
    }

    if (
      !('southWest' in data) ||
      typeof data.southWest !== 'object' ||
      data.southWest === null
    ) {
      throw new Error('data.southWest must be an object');
    }

    if (
      !('northEast' in data) ||
      typeof data.northEast !== 'object' ||
      data.northEast === null
    ) {
      throw new Error('data.northEast must be an object');
    }

    const {southWest, northEast} = data;

    if (!('lat' in southWest) || typeof southWest.lat !== 'number') {
      throw new Error('data.southWest.lat must be a number');
    }

    if (!('lng' in southWest) || typeof southWest.lng !== 'number') {
      throw new Error('data.southWest.lng must be a number');
    }

    if (!('lat' in northEast) || typeof northEast.lat !== 'number') {
      throw new Error('data.northEast.lat must be a number');
    }

    if (!('lng' in northEast) || typeof northEast.lng !== 'number') {
      throw new Error('data.northEast.lng must be a number');
    }

    if ('page' in data && typeof data.page !== 'number') {
      throw new Error('data.page must be a number');
    }

    if ('pageSize' in data && typeof data.pageSize !== 'number') {
      throw new Error('data.pageSize must be a number');
    }

    if ('q' in data && data.q !== undefined && typeof data.q !== 'string') {
      throw new Error('data.q must be a string');
    }

    if (
      'eventType' in data &&
      data.eventType !== undefined &&
      !validEventTypes.includes(data.eventType as EventTypeValue)
    ) {
      throw new Error(
        `data.eventType must be one of: ${validEventTypes.join(', ')}`
      );
    }

    if ('disciplines' in data && data.disciplines !== undefined) {
      if (!Array.isArray(data.disciplines)) {
        throw new Error('data.disciplines must be an array');
      }
      const validDisciplines = Discipline.options;
      for (const d of data.disciplines) {
        if (!validDisciplines.includes(d)) {
          throw new Error(
            `Invalid discipline "${d}". Must be one of: ${validDisciplines.join(', ')}`
          );
        }
      }
    }

    if ('dateFrom' in data && data.dateFrom !== undefined) {
      if (typeof data.dateFrom !== 'string') {
        throw new Error('data.dateFrom must be a string');
      }
      if (Number.isNaN(Date.parse(data.dateFrom))) {
        throw new Error('data.dateFrom must be a valid date string');
      }
    }

    if ('dateTo' in data && data.dateTo !== undefined) {
      if (typeof data.dateTo !== 'string') {
        throw new Error('data.dateTo must be a string');
      }
      if (Number.isNaN(Date.parse(data.dateTo))) {
        throw new Error('data.dateTo must be a valid date string');
      }
    }

    if (
      'minDistance' in data &&
      data.minDistance !== undefined &&
      typeof data.minDistance !== 'number'
    ) {
      throw new Error('data.minDistance must be a number');
    }

    if (
      'maxDistance' in data &&
      data.maxDistance !== undefined &&
      typeof data.maxDistance !== 'number'
    ) {
      throw new Error('data.maxDistance must be a number');
    }

    return data as BoundsData;
  })
  .handler(async ({data}) => {
    const {southWest, northEast} = data;
    const page = data.page ?? 1;
    const pageSize = data.pageSize ?? 10;
    const skip = (page - 1) * pageSize;
    const prisma = await prismaClient();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tsQuery = data.q ? buildTsQueryString(data.q) : null;
    const eventType = data.eventType;
    const disciplines = data.disciplines;
    const dateFrom = data.dateFrom ? new Date(data.dateFrom) : undefined;
    const dateTo = data.dateTo
      ? new Date(new Date(data.dateTo).setHours(23, 59, 59, 999))
      : undefined;
    const minDistance = data.minDistance;
    const maxDistance = data.maxDistance;

    /**
     * Builds a raw SQL WHERE fragment combining bounds, future-event,
     * eventType, discipline, and date range filters.
     */
    const buildSqlFilter = () => {
      const hiddenTypes = Prisma.join(
        HIDDEN_EVENT_TYPES.map((t) => Prisma.sql`${t}::"EventType"`)
      );

      let filter = Prisma.sql`
        "archived" = false
        AND "lat" >= ${southWest.lat} AND "lat" <= ${northEast.lat}
        AND "lng" >= ${southWest.lng} AND "lng" <= ${northEast.lng}
        AND "eventType" NOT IN (${hiddenTypes})
        AND (
          "endDate" >= ${today}
          OR ("endDate" IS NULL AND "startDate" >= ${today})
          OR ("endDate" IS NULL AND "startDate" IS NULL)
        )
      `;

      if (eventType) {
        filter = Prisma.sql`${filter} AND "eventType" = ${eventType}::"EventType"`;
      }

      if (disciplines && disciplines.length > 0) {
        const values = Prisma.join(
          disciplines.map((d) => Prisma.sql`${d}::"Discipline"`)
        );
        filter = Prisma.sql`${filter} AND "discipline" IN (${values})`;
      }

      if (dateFrom && dateTo) {
        filter = Prisma.sql`${filter} AND (("startDate" >= ${dateFrom} AND "startDate" <= ${dateTo}) OR "startDate" IS NULL)`;
      } else if (dateFrom) {
        filter = Prisma.sql`${filter} AND "startDate" >= ${dateFrom}`;
      } else if (dateTo) {
        filter = Prisma.sql`${filter} AND ("startDate" <= ${dateTo} OR "startDate" IS NULL)`;
      }

      // Filter by route distance (miles → meters)
      if (minDistance || maxDistance) {
        const minMeters = minDistance ? minDistance * 1609.34 : null;
        const maxMeters = maxDistance ? maxDistance * 1609.34 : null;

        let routeCondition = Prisma.sql`"distance_meters" IS NOT NULL`;
        if (minMeters) {
          routeCondition = Prisma.sql`${routeCondition} AND "distance_meters" >= ${minMeters}`;
        }
        if (maxMeters) {
          routeCondition = Prisma.sql`${routeCondition} AND "distance_meters" <= ${maxMeters}`;
        }

        filter = Prisma.sql`${filter} AND EXISTS (
          SELECT 1 FROM "Route" WHERE "Route"."eventId" = "Event"."id" AND ${routeCondition}
        )`;
      }

      return filter;
    };

    // Full-text search path with bounds
    if (tsQuery) {
      const sqlFilter = buildSqlFilter();

      const [{ids, totalCount}, {ids: ghostIdResults}] = await Promise.all([
        searchEventIds(prisma, tsQuery, sqlFilter, pageSize, skip),
        searchEventIds(prisma, tsQuery, sqlFilter, 500 + pageSize, 0),
      ]);

      if (ids.length === 0) {
        return {
          events: [],
          ghostEvents: [],
          pagination: {
            page,
            pageSize,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
          bounds: {southWest, northEast},
        };
      }

      const orderedIds = ids.map((r) => r.id);
      const currentPageIdSet = new Set(orderedIds);
      const ghostIds = ghostIdResults
        .map((r) => r.id)
        .filter((id) => !currentPageIdSet.has(id))
        .slice(0, 500);

      const [events, ghostEventsRaw] = await Promise.all([
        prisma.event.findMany({
          where: {id: {in: orderedIds}},
          include: eventInclude,
        }),
        ghostIds.length > 0
          ? prisma.event.findMany({
              where: {id: {in: ghostIds}},
              select: ghostEventSelect,
            })
          : Promise.resolve([]),
      ]);

      // Re-sort to match FTS rank order
      const eventMap = new Map(events.map((e) => [e.id, e]));
      const sortedEvents = orderedIds
        .map((id) => eventMap.get(id))
        .filter(Boolean);

      const validatedEvents = z.array(Event).parse(sortedEvents);
      const validatedMapEvents = z.array(MapEvent).parse(ghostEventsRaw);
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        events: validatedEvents,
        ghostEvents: validatedMapEvents,
        pagination: {
          page,
          pageSize,
          totalCount,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
        bounds: {southWest, northEast},
      };
    }

    // No search query — use standard Prisma query with bounds
    const whereClause: Record<string, unknown> = {
      archived: false,
      eventType: {
        notIn: HIDDEN_EVENT_TYPES,
        ...(eventType && {equals: eventType}),
      },
      lat: {
        gte: southWest.lat,
        lte: northEast.lat,
      },
      lng: {
        gte: southWest.lng,
        lte: northEast.lng,
      },
      AND: [
        {
          OR: [
            {endDate: {gte: today}},
            {endDate: null, startDate: {gte: today}},
            {endDate: null, startDate: null},
          ],
        },
      ],
    };

    if (disciplines && disciplines.length > 0) {
      whereClause.discipline = {in: disciplines};
    }

    if (dateFrom && dateTo) {
      (whereClause.AND as unknown[]).push({
        OR: [
          {startDate: {gte: dateFrom, lte: dateTo}},
          {startDate: null},
        ],
      });
    } else if (dateFrom) {
      whereClause.startDate = {gte: dateFrom};
    } else if (dateTo) {
      (whereClause.AND as unknown[]).push({
        OR: [{startDate: {lte: dateTo}}, {startDate: null}],
      });
    }

    if (minDistance || maxDistance) {
      whereClause.routes = {
        some: {
          distance_meters: {
            not: null,
            ...(minDistance && {gte: minDistance * 1609.34}),
            ...(maxDistance && {lte: maxDistance * 1609.34}),
          },
        },
      };
    }

    const [events, totalCount, ghostEventsRaw] = await prisma.$transaction([
      prisma.event.findMany({
        where: whereClause,
        skip,
        take: pageSize,
        orderBy: {
          startDate: {sort: 'asc', nulls: 'last'},
        },
        include: eventInclude,
      }),
      prisma.event.count({
        where: whereClause,
      }),
      prisma.event.findMany({
        where: whereClause,
        skip: 0,
        take: 500,
        orderBy: {
          startDate: {sort: 'asc', nulls: 'last'},
        },
        select: ghostEventSelect,
      }),
    ]);

    const validatedEvents = z.array(Event).parse(events);
    const currentPageIdSet = new Set(validatedEvents.map((e) => e.id));
    const validatedMapEvents = z
      .array(MapEvent)
      .parse(ghostEventsRaw)
      .filter((ghost) => !currentPageIdSet.has(ghost.id));
    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      events: validatedEvents,
      ghostEvents: validatedMapEvents,
      pagination: {
        page,
        pageSize,
        totalCount,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
      bounds: {
        southWest,
        northEast,
      },
    };
  });
